import React, { useRef, useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Loader2, Printer, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtShortDate = (date) => `${DAY_SHORT[date.getDay()]} ${date.getDate()}`;
const fmtMonthDate = (date) => `${MONTHS[date.getMonth()]} ${date.getDate()}`;

const TH = {
  padding: '9px 10px',
  borderBottom: '2px solid #e2e8f0',
  borderRight: '1px solid #f1f5f9',
  fontSize: '11px',
  fontWeight: '700',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  background: '#f8fafc',
  color: '#475569',
};

const TD = {
  padding: '7px 8px',
  borderBottom: '1px solid #e2e8f0',
  borderRight: '1px solid #f1f5f9',
  verticalAlign: 'top',
  fontSize: '11px',
};

const TD_NAME = {
  ...TD,
  fontWeight: '600',
  fontSize: '12px',
  color: '#1e293b',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

export function PrintableSchedule({ open, onClose, schedules, staff, weekDates, locationName }) {
  const printRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const weekDateStrings = useMemo(
    () => weekDates.map(d => d.toISOString().split('T')[0]),
    [weekDates]
  );

  // Build employee × day matrix — include all staff
  const rows = useMemo(() => {
    const map = {};
    staff.forEach(s => {
      map[s.id] = { ...s, days: {} };
      weekDateStrings.forEach(ds => { map[s.id].days[ds] = []; });
    });
    schedules.forEach(sch => {
      if (map[sch.user_id] && map[sch.user_id].days[sch.date] !== undefined) {
        map[sch.user_id].days[sch.date].push(sch);
      }
    });
    return Object.values(map);
  }, [schedules, staff, weekDateStrings]);

  const draftCount = schedules.filter(s => s.status === 'draft').length;

  // ─── Generate full HTML for the print window ────────────────────────────────
  const generatePrintHTML = () => {
    const thCells = weekDates.map(d => {
      const isToday = d.toDateString() === new Date().toDateString();
      return `<th style="padding:9px 10px;border-bottom:2px solid #e2e8f0;border-right:1px solid #f1f5f9;font-size:11px;font-weight:700;text-align:center;background:${isToday ? '#fff7ed' : '#f8fafc'};color:${isToday ? '#f97316' : '#475569'};text-transform:uppercase;letter-spacing:0.04em;">${fmtShortDate(d)}</th>`;
    }).join('');

    const tableRows = rows.map((row, ri) => {
      const cells = weekDateStrings.map(ds => {
        const shifts = row.days[ds] || [];
        const cellContent = shifts.length === 0
          ? '<span style="color:#cbd5e1;font-size:11px;">–</span>'
          : shifts.map(sh => `
              <div style="padding:3px 5px;border-radius:3px;border-left:3px solid ${sh.role_color || '#94a3b8'};background:${sh.role_color ? sh.role_color + '18' : '#f1f5f9'};margin-bottom:3px;">
                <div style="font-weight:600;font-size:11px;color:#0f172a;">${sh.start_time}–${sh.end_time}${sh.status === 'draft' ? ' <span style="font-size:9px;color:#94a3b8;font-weight:400;">(draft)</span>' : ''}</div>
                ${sh.role_name ? `<div style="font-size:10px;color:${sh.role_color || '#64748b'};font-weight:500;">${sh.role_name}</div>` : ''}
              </div>
            `).join('');
        return `<td style="padding:7px 8px;border-bottom:1px solid #e2e8f0;border-right:1px solid #f1f5f9;vertical-align:top;">${cellContent}</td>`;
      }).join('');

      return `<tr style="background:${ri % 2 === 0 ? '#fff' : '#f8fafc'}">
        <td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;border-right:1px solid #e2e8f0;font-weight:600;font-size:12px;color:#1e293b;white-space:nowrap;">${row.name}</td>
        ${cells}
      </tr>`;
    }).join('');

    const draftNote = draftCount > 0
      ? `<div style="margin-bottom:14px;padding:7px 12px;background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;font-size:11px;color:#92400e;">${draftCount} draft shift${draftCount !== 1 ? 's' : ''} included — publish the schedule so employees can see their shifts.</div>`
      : '';

    return `<!DOCTYPE html><html>
<head>
  <meta charset="utf-8">
  <title>Schedule – ${locationName.replace(/</g, '&lt;')}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#0f172a;background:#fff;padding:24px 28px}
    @page{size:A4 landscape;margin:12mm 14mm}
    @media print{body{padding:0}}
    table{page-break-inside:auto;width:100%;border-collapse:collapse}
    tr{page-break-inside:avoid;page-break-after:auto}
  </style>
</head>
<body>
  <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;padding-bottom:14px;border-bottom:2px solid #f97316">
    <div style="display:flex;align-items:center;gap:10px">
      <div style="background:#f97316;border-radius:6px;width:32px;height:32px;display:flex;align-items:center;justify-content:center;flex-shrink:0">
        <span style="color:#fff;font-weight:800;font-size:12px">RS</span>
      </div>
      <div>
        <div style="font-weight:700;font-size:16px;color:#0f172a">${locationName.replace(/</g, '&lt;')}</div>
        <div style="font-size:11px;color:#64748b;margin-top:2px">Weekly Schedule · ${fmtMonthDate(weekDates[0])} – ${fmtMonthDate(weekDates[6])}, ${weekDates[0].getFullYear()}</div>
      </div>
    </div>
    <div style="text-align:right;font-size:10px;color:#94a3b8">
      <div>Printed ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
      <div>getrestaff.com</div>
    </div>
  </div>
  ${draftNote}
  <table>
    <thead>
      <tr>
        <th style="padding:9px 10px;border-bottom:2px solid #e2e8f0;border-right:1px solid #e2e8f0;font-size:11px;font-weight:700;text-align:left;background:#f8fafc;color:#475569;text-transform:uppercase;letter-spacing:0.04em;min-width:110px">Employee</th>
        ${thCells}
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
  <div style="margin-top:14px;padding-top:10px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8">
    <span>Total shifts: ${schedules.length}${draftCount > 0 ? ` (${draftCount} draft)` : ''}</span>
    <span>Generated by ReStaff · getrestaff.com</span>
  </div>
</body>
</html>`;
  };

  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=1200,height=850');
    if (!win) {
      toast.error('Popup blocked. Please allow popups for this site.');
      return;
    }
    win.document.write(generatePrintHTML());
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const handleExportPDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      const canvas = await html2canvas(printRef.current, {
        scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pxW = canvas.width;
      const pxH = canvas.height;
      const mmW = 297;
      const mmH = (pxH * mmW) / pxW;
      const orientation = mmH > mmW ? 'portrait' : 'landscape';
      const pdf = new jsPDF({ orientation, unit: 'mm', format: [mmW, mmH] });
      pdf.addImage(imgData, 'PNG', 0, 0, mmW, mmH);
      const safe = locationName.replace(/[^a-zA-Z0-9]/g, '');
      const dateStr = weekDates[0].toISOString().split('T')[0];
      pdf.save(`${safe}_Schedule_${dateStr}.pdf`);
      toast.success('PDF downloaded — ready to share!');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-white flex flex-col"
      role="dialog"
      aria-modal="true"
      data-testid="print-schedule-overlay"
    >
      {/* ── Toolbar (hidden when printing) ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#f97316' }}>
            <span className="text-white font-bold text-sm">RS</span>
          </div>
          <div>
            <p className="font-semibold text-slate-900 text-sm">Print Preview</p>
            <p className="text-xs text-slate-500">
              {locationName} · {fmtMonthDate(weekDates[0])} – {fmtMonthDate(weekDates[6])}, {weekDates[0].getFullYear()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            className="flex items-center gap-1.5"
            data-testid="print-btn"
          >
            <Printer size={14} />
            Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center gap-1.5"
            data-testid="pdf-download-btn"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            Download PDF
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="print-close-btn"
          >
            <X size={16} />
          </Button>
        </div>
      </div>

      {/* ── Print preview area ── */}
      <div className="flex-1 overflow-auto bg-slate-100 p-6">
        <div className="max-w-[1100px] mx-auto bg-white rounded-lg shadow-sm p-8" ref={printRef}>

          {/* Report header */}
          <div
            className="flex items-center justify-between mb-5 pb-4"
            style={{ borderBottom: '2px solid #f97316' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: '#f97316' }}
              >
                <span className="text-white font-extrabold text-sm">RS</span>
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">{locationName}</p>
                <p className="text-xs text-slate-500">
                  Weekly Schedule · {fmtMonthDate(weekDates[0])} – {fmtMonthDate(weekDates[6])}, {weekDates[0].getFullYear()}
                </p>
              </div>
            </div>
            <div className="text-right text-xs text-slate-400">
              <p>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p>getrestaff.com</p>
            </div>
          </div>

          {/* Draft notice */}
          {draftCount > 0 && (
            <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
              {draftCount} draft shift{draftCount !== 1 ? 's' : ''} included. Publish the schedule for employees to see their shifts.
            </div>
          )}

          {/* Schedule table */}
          {rows.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">
              No staff or shifts found for this week.
            </div>
          ) : (
            <table className="w-full border-collapse" style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  <th style={{ ...TH, textAlign: 'left', minWidth: '120px' }}>Employee</th>
                  {weekDates.map((date, i) => {
                    const isToday = date.toDateString() === new Date().toDateString();
                    return (
                      <th
                        key={i}
                        style={{
                          ...TH,
                          textAlign: 'center',
                          background: isToday ? '#fff7ed' : '#f8fafc',
                          color: isToday ? '#f97316' : '#475569',
                        }}
                      >
                        {fmtShortDate(date)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={row.id} style={{ background: ri % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                    <td style={TD_NAME}>{row.name}</td>
                    {weekDateStrings.map(ds => {
                      const shifts = row.days[ds] || [];
                      return (
                        <td key={ds} style={TD}>
                          {shifts.length === 0 ? (
                            <span style={{ color: '#cbd5e1' }}>–</span>
                          ) : (
                            shifts.map((shift, si) => (
                              <div
                                key={si}
                                style={{
                                  padding: '3px 6px',
                                  borderRadius: '4px',
                                  borderLeft: `3px solid ${shift.role_color || '#94a3b8'}`,
                                  background: shift.role_color ? `${shift.role_color}18` : '#f1f5f9',
                                  marginBottom: si < shifts.length - 1 ? '3px' : 0,
                                  opacity: shift.status === 'draft' ? 0.75 : 1,
                                }}
                              >
                                <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '11px' }}>
                                  {shift.start_time}–{shift.end_time}
                                  {shift.status === 'draft' && (
                                    <span style={{ marginLeft: '4px', fontSize: '9px', color: '#94a3b8', fontWeight: '400' }}>
                                      (draft)
                                    </span>
                                  )}
                                </div>
                                {shift.role_name && (
                                  <div style={{ fontSize: '10px', fontWeight: '500', color: shift.role_color || '#64748b' }}>
                                    {shift.role_name}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Footer */}
          <div
            className="mt-4 pt-3 flex justify-between text-xs text-slate-400"
            style={{ borderTop: '1px solid #e2e8f0' }}
          >
            <span>Total shifts: {schedules.length}{draftCount > 0 ? ` (${draftCount} draft)` : ''}</span>
            <span>Generated by ReStaff · getrestaff.com</span>
          </div>

        </div>
      </div>
    </div>
  );
}
